import { InjectionToken, NgModule } from '@angular/core';
import { NgxsModule, Store } from '@ngxs/store';
import {
  ActionRequiredState,
  AppEnvironmentState,
  FeatureFlagConfigurationState,
  GeaMissingTranslationHandler,
  GeaTranslateHttpLoader,
  HeaderConfig,
  HeaderModule,
  LIBRARY_CONFIG,
  PageContentLayoutComponent,
  PageHeaderModule,
  PageLayoutModule,
  PermissionsState,
  ProgressSpinnerComponent,
  SidebarV3Component,
  UserState,
} from '@gea/digital-ui-lib';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { Configuration as InsightPartnerConfiguration } from './generated-apis/insightpartner-api/v1';

import { AppComponent } from './app.component';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '../environments/models/environment.model';
import { environment } from '@environments/environment';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxEchartsModule } from 'ngx-echarts';
import { PreloadAllModules, provideRouter, RouterOutlet, withPreloading } from '@angular/router';
import { routes } from './app.routes';
import { provideEchartsTheme } from '@gea/digital-charts-ui-lib';
import { ReportGenerationState } from '@shared/redux/report-generation.state';

export const HEADER_CONFIG = new InjectionToken<HeaderConfig>('HeaderConfig');

// <CHANGE ME> - Change the path to your files
function HttpLoaderFactory(http: HttpClient, environment: EnvironmentConfiguration) {
  return new GeaTranslateHttpLoader(http, environment.localizationStorageURL, ['i18n/1/insightpartner-sep-monitoring/']);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    NgxsModule.forRoot(
      [
        UserState,
        ActionRequiredState,
        AppEnvironmentState,
        PermissionsState,
        FeatureFlagConfigurationState,
        ReportGenerationState,
      ],
      {
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      }
    ),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    BrowserAnimationsModule,
    HeaderModule.forRoot(HEADER_CONFIG),
    PageLayoutModule,
    SidebarV3Component,
    PageContentLayoutComponent,
    PageHeaderModule,
    ProgressSpinnerComponent,
    NgxEchartsModule.forRoot({
      echarts: () => import('./echarts.setup').then((m) => m.echarts),
    }),
    TranslateModule.forRoot({
      extend: true,
      defaultLanguage: 'en-US',
      useDefaultLang: false,
      loader: {
        deps: [HttpClient, ENVIRONMENT_CONFIG],
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: GeaMissingTranslationHandler,
      },
    }),
    ProgressSpinnerModule,
    HeaderModule,
    RouterOutlet,
  ],
  providers: [
    provideEchartsTheme(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    {
      provide: LIBRARY_CONFIG,
      useFactory: (env: EnvironmentConfiguration) => {
        return {
          ...env,
          baseUrl: env.portal.baseURL ?? env.baseURL,
          backendInfoUrl: (env.portal.baseURL ?? env.baseURL) + 'geaid/v1/info',
          legalDocumentsStorageURL: env.storageURL,
        };
      },
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: InsightPartnerConfiguration,
      useFactory: (env: EnvironmentConfiguration, store: Store) => {
        return new InsightPartnerConfiguration({
          basePath: env.apisBaseUrl.insightPartner,
          credentials: {
            bearerAuth: () => store.selectSnapshot(UserState.token),
          },
        });
      },
      deps: [ENVIRONMENT_CONFIG, Store],
    },
    {
      provide: 'baseUrl',
      useFactory: (env: EnvironmentConfiguration) => env.portal.baseURL ?? env.baseURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'storageURL',
      useFactory: (env: EnvironmentConfiguration) => env.localizationStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'appContentPath',
      useFactory: (env: EnvironmentConfiguration) => env.appContentStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
